export interface Hora {
  startHour: string;
  endHour: string;
}

export interface HourOperation {
  timeStart: string;
  timeEnd: string;
  dayOfWeek: number;
}

export interface DiaHorario {
  dia: string;
  active: boolean;
  hours: Hora[];
}


export interface OrganizacaoHorario {
    timeStart: string;
    timeEnd: string;
    dayOfWeek: number | string;
}

const dayNames: { [key: number]: string } = {
  1: 'Segunda-feira',
  2: 'Terça-feira',
  3: 'Quarta-feira',
  4: 'Quinta-feira',
  5: 'Sexta-feira',
  6: 'Sábado',
  0: 'Domingo'
};


export function toMinutes(time: string): number {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
}


export function hasOverlappingHours(hours: Hora[]): boolean {
  const sorted = hours
    .map(h => ({ start: toMinutes(h.startHour), end: toMinutes(h.endHour) }))
    .sort((a, b) => a.start - b.start);

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].start < sorted[i - 1].end) {
      return true;
    }
  }
  return false;
}


export function hasOverlappingOperationHours(hours: HourOperation[]): string[] {
  const groupedByDay: { [key: number]: HourOperation[] } = {};
  const invalidHours: string[] = [];

  for (const hour of hours) {
    if (!groupedByDay[hour.dayOfWeek]) {
      groupedByDay[hour.dayOfWeek] = [];
    }
    groupedByDay[hour.dayOfWeek].push(hour);
  }

  for (const day in groupedByDay) {
    const dayNumber = Number(day);
    const intervals = groupedByDay[dayNumber]
      .map(h => ({
        start: toMinutes(h.timeStart),
        end: toMinutes(h.timeEnd)
      }))
      .sort((a, b) => a.start - b.start);

    for (let i = 1; i < intervals.length; i++) {
      if (intervals[i].start < intervals[i - 1].end) {
        invalidHours.push(`${dayNames[dayNumber]} (sobreposição de horários)`);
        break; 
      }
    }
  }

  return invalidHours;
}


export function isAtLeastOneDayFilled(hours: DiaHorario[]): boolean {
  return hours.some(h => h.active && h.hours.length > 0);
}


export function isWithinOrganizationLimits(
  dia: string,
  hours: Hora[],
  organizationHours: OrganizacaoHorario[],
  getDayIndex: (dia: string) => number
): boolean {
  const dayIndex = getDayIndex(dia);
  const orgDay = organizationHours.find(o => o.dayOfWeek === dayIndex);
  if (!orgDay) return false;

  const orgStart = toMinutes(orgDay.timeStart);
  const orgEnd = toMinutes(orgDay.timeEnd);

  return hours.every(h => {
    const start = toMinutes(h.startHour);
    const end = toMinutes(h.endHour);
    return start >= orgStart && end <= orgEnd;
  });
}


export function validatePractitionerHours(
  practitionerHours: DiaHorario[],
  organizationHours: OrganizacaoHorario[],
  getDayIndex: (dia: string) => number
): { invalidHours: string[]; validHours: string[] } {
  const invalidHours: string[] = [];
  const validHours: string[] = [];

  for (const hour of practitionerHours) {
    if (hour.active && hour.hours.length > 0) {

      if (hasOverlappingHours(hour.hours)) {
        invalidHours.push(`${hour.dia} (sobreposição de horários)`);
        continue;
      }

      if (!isWithinOrganizationLimits(hour.dia, hour.hours, organizationHours, getDayIndex)) {
        const orgDay = organizationHours.find(
          o => o.dayOfWeek === getDayIndex(hour.dia)
        );

        invalidHours.push(`${hour.dia}: ${hour.hours.map(h => `${h.startHour} - ${h.endHour}`).join(', ')}`);

        if (orgDay) {
          validHours.push(`${hour.dia}: ${orgDay.timeStart} - ${orgDay.timeEnd}`);
        }
      }
    }
  }

  return { invalidHours, validHours };
}